// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// IndexedDB, safely (M3-39).
//
// The other half of `local.ts`, and a different shape for a good reason. `localStorage` is
// synchronous, about five megabytes, and stores strings — which is right for a theme choice and
// hopeless for a replay: a twelve-minute game's input trace is tens of thousands of events, and
// writing one synchronously would stall the frame it is written on. This is asynchronous,
// structured-clone rather than JSON, and does not block the machine.
//
// **Everything here answers with an absence rather than an exception**, exactly as `local.ts`
// does, and for the same reason with more force. IndexedDB is missing in some private modes,
// blocked entirely behind "block all cookies", refused when the origin's quota is full, and — the
// one that is easy to forget — it can be *evicted* between two visits without telling anybody. A
// player whose replay could not be saved has lost a replay. A player whose machine will not start
// because a replay could not be saved has lost the product.
//
// One store, keyed by string. Not because a schema would be wrong later, but because a schema is
// a migration surface, and the whole of what this holds — a high-score table, three replays and
// one interrupted game — is a handful of documents that are cheap to re-derive and safe to lose.

/** The database, and the single object store inside it. */
export const DB_NAME = 'hcc.pinball'
export const STORE_NAME = 'documents'

/** Bumped only when the object store itself changes shape, which is not the same as the data. */
export const DB_VERSION = 1

/** A string-keyed document store. The four calls this product makes, and no more. */
export interface DocumentStore {
  get: <T>(key: string) => Promise<T | undefined>
  put: (key: string, value: unknown) => Promise<boolean>
  remove: (key: string) => Promise<void>
  /** Every key, so a caller can find "the three most recent" without a second index. */
  keys: () => Promise<string[]>
}

/** The subset of the IndexedDB API this uses, so a test can supply one without a browser. */
export interface IndexedDbLike {
  open: (name: string, version?: number) => IDBOpenDBRequest
}

/**
 * Open the store, or answer nothing.
 *
 * Never throws and never rejects. A caller that had to handle a failure would end up writing the
 * same `catch` at every site, and one of them would eventually be the one that takes the page
 * down — so the failure is spent here and what comes out is either a working store or nothing at
 * all.
 */
export async function openDocuments(
  factory: IndexedDbLike | undefined = (globalThis as { indexedDB?: IndexedDbLike }).indexedDB,
): Promise<DocumentStore | undefined> {
  if (factory === undefined) return undefined
  let db: IDBDatabase
  try {
    db = await new Promise<IDBDatabase>((resolve, reject) => {
      const request = factory.open(DB_NAME, DB_VERSION)
      request.onupgradeneeded = () => {
        const upgrading = request.result
        if (!upgrading.objectStoreNames.contains(STORE_NAME)) upgrading.createObjectStore(STORE_NAME)
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error ?? new Error('indexedDB refused to open'))
      // Another tab holding an older version open. Rejecting rather than waiting is deliberate:
      // the machine must start now, and the worst case is that this session keeps no history.
      request.onblocked = () => reject(new Error('indexedDB is blocked by another tab'))
    })
  } catch {
    return undefined
  }

  const run = async <T>(mode: IDBTransactionMode, body: (store: IDBObjectStore) => IDBRequest): Promise<T | undefined> => {
    try {
      return await new Promise<T | undefined>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, mode)
        const request = body(tx.objectStore(STORE_NAME))
        request.onsuccess = () => resolve(request.result as T)
        request.onerror = () => reject(request.error ?? new Error('indexedDB request failed'))
        tx.onabort = () => reject(tx.error ?? new Error('indexedDB transaction aborted'))
      })
    } catch {
      return undefined
    }
  }

  return {
    get: <T,>(key: string) => run<T>('readonly', (store) => store.get(key)),
    put: async (key, value) => {
      // `structuredClone` semantics, so a trace goes in as objects and comes out as objects with
      // no `JSON.stringify` in the middle. That matters at replay size: stringifying forty
      // thousand events costs more than the write.
      const done = await run<IDBValidKey>('readwrite', (store) => store.put(value, key))
      return done !== undefined
    },
    remove: async (key) => {
      await run<undefined>('readwrite', (store) => store.delete(key))
    },
    keys: async () => {
      const found = await run<IDBValidKey[]>('readonly', (store) => store.getAllKeys())
      // Every key this product writes is a string; anything else came from somewhere else and
      // is not ours to interpret.
      return (found ?? []).filter((k): k is string => typeof k === 'string')
    },
  }
}
